import { inject, Injectable } from '@angular/core'
import { ApiEmobility } from '@cr-driver/api/api.emobility'
import { centToEuro, openImageInNewTab } from '@cr-driver/charge-repay-driver.utils'
import { ErrorHandler } from '@cr-driver/handler/error.handler'
import { ChargeRepayErrorCodes, PCIAMErrorCodes } from '@cr-driver/models.errors'
import {
  IAccountDetails,
  IBackendProvider,
  ICar,
  ICarWithMileages,
  ICharge,
  IChargeCompleted,
  IChargePoint,
  IChargeRepayAPIError,
  IChargeRepayError,
  IConfigCode,
  IDriver,
  IExportData,
  IExportDocumentFile,
  IGatewayConfig,
  IMileage,
  IMonthYear,
  IPrice,
  IPriceInformation,
  IRFIDTag,
} from '@cr-driver/models.interfaces'
import { environment } from '@cr-environments/environment'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concat,
  distinctUntilChanged,
  EMPTY,
  forkJoin,
  interval,
  map,
  Observable,
  of,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs'
import { filter } from 'rxjs/operators'

@Injectable({
  providedIn: 'root',
})
export class DriverStore {
  public isInitialized = false

  // Dependencies
  private errorHandler = inject(ErrorHandler)
  private apiEmobility = inject(ApiEmobility)

  private driverSubject$ = new BehaviorSubject<IDriver | null>(null)
  public readonly driver$ = this.driverSubject$.asObservable()

  private chargePointConfigSubject$ = new BehaviorSubject<IGatewayConfig | null>(null)
  public readonly chargePointConfig$ = this.chargePointConfigSubject$.asObservable()

  private chargePointDefaultConfigSubject$ = new BehaviorSubject<IGatewayConfig | null>(null)
  public readonly chargePointDefaultConfig$ = this.chargePointDefaultConfigSubject$.asObservable()

  private chargePointSubject$ = new BehaviorSubject<IChargePoint | null>(null)
  public readonly chargePoint$ = this.chargePointSubject$.asObservable()

  private priceInformationSubject$ = new BehaviorSubject<IPriceInformation | null>(null)
  public readonly princeInformation$ = this.priceInformationSubject$.asObservable()

  private chargePointConnectedSubject$ = new BehaviorSubject<boolean>(false)
  public readonly chargePointConnected$ = this.chargePointConnectedSubject$.asObservable()

  private chargesSubject$ = new BehaviorSubject<ICharge[]>([])
  public readonly charges$ = this.chargesSubject$.asObservable()

  private activeChargesSubject$ = new BehaviorSubject<ICharge[]>([])
  public readonly activeCharges$ = this.activeChargesSubject$.asObservable()

  private selectedMonthSubject$ = new BehaviorSubject<IMonthYear | null>(null)
  public readonly selectedMonth$ = this.selectedMonthSubject$.asObservable()

  private accountDetailsSubject$ = new BehaviorSubject<IAccountDetails | null>(null)
  public readonly accountDetails$ = this.accountDetailsSubject$.asObservable()

  private carSubject$ = new BehaviorSubject<ICarWithMileages | null>(null)
  public readonly car$ = this.carSubject$.asObservable()

  private configurationCodeSubject$ = new BehaviorSubject<IConfigCode | null>(null)
  public readonly configurationCode$ = this.configurationCodeSubject$.asObservable()

  private chargePointEverConnectionSubject$ = new BehaviorSubject<boolean>(false)
  public readonly chargePointEverConnected$ = this.chargePointEverConnectionSubject$.asObservable()

  private rfidTagsSubject$ = new BehaviorSubject<IRFIDTag[]>([])
  public readonly rfidTags$ = this.rfidTagsSubject$.asObservable()

  private backendProviderSubject$ = new BehaviorSubject<IBackendProvider | null>(null)
  public readonly backendProvider$ = this.backendProviderSubject$.asObservable()

  /*
   * This method queries the necessary data for the charge repay driver Store
   * It will be triggered by an outside component which represents the root component,
   * Therefore this function is not called in a constructor
   * The content of this store is connected to all driver related data but outermost the driver itself
   * ERROR: Injects an error object into the charge repay error stream. Logout should be the consequence.
   */
  public initializeStore$() {
    this.isInitialized = true

    // Concat runs all given observables in sequence and waits for them to complete it also closes the subscription automatically no need for further intervention
    return concat(
      this.createDriver$(),
      this.createChargePoint$(),
      this.createAccountDetails$(),
      this.createConfigCode$(),
      this.createCarWithMileages$(),
      this.createChargePointPrice$(),
      this.wasChargePointEverConnected$(),
      this.createRFIDTags$()
    ).pipe(
      tap({
        complete: () => {
          // Start listeners only after all observables above complete, to avoid double calls like the price!!
          this.chargePointListener()
          this.updateChargesListener()
          this.deviceStatePollingClient()
          this.activeChargesPollingClient()
        },
      })
    )
  }

  /*
   * Gets the driver information from the emob backend
   * ErrorHandling: PANIC
   */
  private createDriver$() {
    return this.apiEmobility.getDriver().pipe(
      switchMap((driver) => {
        if (driver) {
          if (driver.driver_has_subscription) {
            this.driverSubject$.next(driver)
            return of('Driver data successfully loaded.')
          } else {
            const driverError: IChargeRepayError = {
              severity: 'panic',
              errorCode: ChargeRepayErrorCodes.BILLING_NOT_ASSIGNED,
            }

            return throwError(() => driverError)
          }
        } else {
          return throwError(() => ({
            severity: 'panic',
            errorCode: ChargeRepayErrorCodes.GET_DRIVER_ERROR,
          }))
        }
      }),
      catchError((err) => {
        if ('severity' in err && 'errorCode' in err) {
          // Already an IChargeRepayError
          return throwError(() => err)
        }

        return throwError(() => ({
          severity: 'panic',
          errorCode: (err as IChargeRepayAPIError).error_code,
        }))
      })
    )
  }

  /*
   * Listening to the charge point being set and update data depending on it
   * This will not run for the first value getting emitted to avoid double calls
   */
  private chargePointListener() {
    this.chargePoint$
      .pipe(
        skip(1),
        filter((cp): cp is IChargePoint => !!cp), // Ensure the charge point is valid
        switchMap(() => forkJoin([this.createChargePointPrice$(), this.wasChargePointEverConnected$(), this.createRFIDTags$()]))
      )
      .subscribe()
  }

  /*
   * Loads charge points from API and updates the subject
   * Returns an observable of the loaded charge points
   */
  private loadChargePoints$(): Observable<IChargePoint[]> {
    return this.apiEmobility.getChargePoints().pipe(
      tap((chargePoints: IChargePoint[]) => {
        if (chargePoints && chargePoints.length > 0) {
          this.chargePointSubject$.next(chargePoints[0])
        }
      })
    )
  }

  /*
   * Gets the drivers charge-points
   * The first and most important data is the list of charge points of this organization
   * The API returns an array but currently there is no plan to allow multiple charge points
   */
  private createChargePoint$(): Observable<string> {
    return this.loadChargePoints$().pipe(
      map(() => 'Successfully created Charge Points'),
      catchError((err: IChargeRepayAPIError) => {
        const error: IChargeRepayError = {
          severity: 'panic',
          errorCode: err.error_code,
        }
        return throwError(() => error)
      })
    )
  }

  private createChargePointPrice$() {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP: IChargePoint | null) => {
        if (!cP) {
          return of(null)
        }

        return this.apiEmobility.getChargePointPrice(cP.uuid).pipe(
          filter((priceInfo): priceInfo is IPriceInformation => !!priceInfo),
          // tap is designed for performing actions (like state updates) that don’t alter the observable’s data flow like a switch map, this is why we use tap only.
          tap((priceInfo) => {
            this.priceInformationSubject$.next(priceInfo)
          }),
          catchError((err: IChargeRepayAPIError) => {
            const getPriceError: IChargeRepayError = {
              severity: 'error',
              errorCode: err.error_code,
            }
            this.errorHandler.handleError(getPriceError)
            // returning empty is necessary because catchError operator needs to return something
            return EMPTY
          })
        )
      })
    )
  }

  public createRFIDTags$() {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP: IChargePoint | null) => {
        if (!cP || !cP?.direct_ocpp) {
          return of(null)
        }

        return this.apiEmobility.getRFIDTags(cP.uuid).pipe(
          tap((tags) => {
            this.rfidTagsSubject$.next(tags)
          }),
          catchError((err: IChargeRepayAPIError) => {
            const getRFIDError: IChargeRepayError = {
              severity: 'error',
              errorCode: err.error_code,
            }
            this.errorHandler.handleError(getRFIDError)
            // returning empty is necessary because catchError operator needs to return something
            return EMPTY
          })
        )
      })
    )
  }

  public setChargePoint(cp: IChargePoint) {
    this.chargePointSubject$.next(cp)
  }

  /*
   * Queries the driver details
   * It is not intended to create a global driver object but rather objects of interests e.g:
   * Instead of creating a driver$ object an accountDetails$ object holding only the account information part of the driver information
   */
  private createAccountDetails$() {
    return this.driver$.pipe(
      take(1), // Ensure only the first emission is processed
      tap((driver: IDriver | null) => {
        if (driver && driver.iban && driver.bank_account_holder && driver.bic) {
          const accountDetails: IAccountDetails = {
            accountOwner: driver.bank_account_holder,
            BIC: driver.bic,
            IBAN: driver.iban,
          }
          this.accountDetailsSubject$.next(accountDetails)
        } else {
          this.accountDetailsSubject$.next(null)
        }
      }),
      map(() => 'Successfully created Account Details'),
      catchError(() => {
        const accountError: IChargeRepayError = {
          severity: 'error',
          errorCode: ChargeRepayErrorCodes.GET_ACCOUNT_INFORMATION_ERROR,
        }
        this.errorHandler.handleError(accountError)
        return EMPTY
      })
    )
  }

  /*
   * Update the account data of the driver
   */
  public updateAccountDetails(details: IAccountDetails) {
    return this.apiEmobility.updateAccountDetails(details).pipe(
      tap(() => {
        this.accountDetailsSubject$.next(details)
      }),
      catchError(() => {
        const accountError: IChargeRepayError = {
          severity: 'error',
          errorCode: ChargeRepayErrorCodes.UPDATE_ACCOUNT_INFORMATION_ERROR,
        }

        return throwError(() => accountError)
      })
    )
  }

  /*
   * Whenever the charge point is loaded/updated, this function will gather the charges
   */
  private updateChargesListener() {
    this.chargePointSubject$.subscribe({
      next: (chPnt) => {
        if (chPnt) {
          this.apiEmobility.getCharges(chPnt.uuid).subscribe({
            next: (charges) => {
              this.chargesSubject$.next(charges)
            },
            error: (err: IChargeRepayAPIError) => {
              // Only error if not 404
              if (err.error_code !== ChargeRepayErrorCodes.CHARGES_NOT_FOUND_ERROR) {
                const error: IChargeRepayError = {
                  severity: 'error',
                  errorCode: err.error_code,
                }
                this.errorHandler.handleError(error)
              }
            },
          })
        }
      },
    })
  }

  /*
   * Whenever the charge point is loaded/updated, this function will gather the "ever connected" flag
   */
  public wasChargePointEverConnected$() {
    return this.chargePointSubject$.pipe(
      take(1),
      filter((cP: IChargePoint) => !!cP),
      switchMap((chPnt: IChargePoint) =>
        this.apiEmobility.getEverConnectedState(chPnt.endpoint_id).pipe(
          tap((wasConnected: boolean) => {
            this.chargePointEverConnectionSubject$.next(wasConnected)
          })
        )
      )
    )
  }

  /*
   * This function will gather the device state (online/offline) on a given interval
   * This will only happen if a charge point is present
   * In addition it will be triggered initially at 0 milliseconds (startWith)
   * If the state changes we need to reload the charge point.
   */
  private deviceStatePollingClient() {
    this.chargePointSubject$
      .pipe(
        switchMap((chPnt) => {
          if (chPnt) {
            return interval(environment.connectionStatePollingInterval).pipe(
              startWith(0),
              switchMap(() => this.apiEmobility.getConnectionState(chPnt.endpoint_id))
            )
          } else {
            return EMPTY
          }
        }),
        distinctUntilChanged(),
        switchMap((connected) => {
          // When the connection state changes, reload charge points from API
          return this.loadChargePoints$().pipe(map(() => connected))
        })
      )
      .subscribe({
        next: (connected) => {
          this.chargePointConnectedSubject$.next(connected)
        },
        error: (err: IChargeRepayAPIError) => {
          const error: IChargeRepayError = {
            severity: 'warning',
            errorCode: err.error_code,
          }
          this.errorHandler.handleError(error)
        },
      })
  }

  /*
   * Updates the charges periodically and updates them.
   * This is not the best approach of doing this, an event pattern based on a static connection like SSE or WS would be much better
   * If a charge completes (charges list goes from non-empty to empty), reload the charge point after 2 seconds
   */
  private activeChargesPollingClient() {
    let hadCharges = false

    this.chargePointSubject$
      .pipe(
        filter((cp): cp is IChargePoint => !!cp),
        switchMap((chPnt) =>
          timer(0, environment.chargesPollingInterval).pipe(
            switchMap(() =>
              this.apiEmobility.getActiveCharges(chPnt.uuid).pipe(
                catchError((err: any) => {
                  const status = err?.status ?? err?.originalError?.status
                  const errorCode = err?.error_code

                  const isCanceled = status === 0 || err?.isSafariAbort
                  const isNotFound = errorCode === ChargeRepayErrorCodes.CHARGES_NOT_FOUND_ERROR

                  if (!isCanceled && !isNotFound) {
                    this.errorHandler.handleError({
                      severity: 'error',
                      errorCode: errorCode || ChargeRepayErrorCodes.GET_ACTIVE_CHARGES_ERROR,
                    })
                  }
                  return EMPTY
                })
              )
            )
          )
        )
      )
      .subscribe((charges) => {
        if (charges && charges.length > 0) {
          this.activeChargesSubject$.next(charges)
          hadCharges = true
        } else if (charges && charges.length === 0 && hadCharges) {
          // Charge completed - charges list went from non-empty to empty
          this.activeChargesSubject$.next(charges)
          hadCharges = false
          // Reload charge point after 2 seconds
          setTimeout(() => {
            this.loadChargePoints$().pipe(take(1)).subscribe()
          }, 2000)
        }
      })
  }

  /**
   * Gets the backend provider information from the IAM service
   * Error severity: ERROR
   * @returns Observable<IBackendProviderResponse>
   * @throws PCIAMErrorCodes.GET_BACKEND_PROVIDER_ERROR
   * @public
   */
  public createBackendProvider$() {
    return this.apiEmobility.getBackendProviderData().pipe(
      tap((backendProvider) => {
        if (backendProvider) {
          const _backendProvider: IBackendProvider = {
            providerId: backendProvider.provider_id,
            providerName: backendProvider.provider_name,
            providerType: backendProvider.provider_type,
            providerUrl: backendProvider.provider_url,
          }
          this.backendProviderSubject$.next(_backendProvider)
        }
      }),
      catchError(() => {
        const error: IChargeRepayError = {
          severity: 'error',
          errorCode: ChargeRepayErrorCodes.GET_BACKEND_PROVIDER_ERROR,
        }
        return throwError(() => error)
      })
    )
  }

  /*
   * Updates the selected MonthYear entity
   */
  public setSelectedMonthYear(month: IMonthYear) {
    if (month) {
      this.selectedMonthSubject$.next(month)
    } else {
      console.warn('no month was given')
    }
  }

  /**
   * Gets the driver CSV for a given month and year
   * It requires the charge point and the selected month to be set, otherwise it will throw an error
   * The error will be catched in the component and shown to the user
   */
  public getDriverCSV(): Observable<{ blob: Blob; filename: string }> {
    return combineLatest([this.chargePoint$, this.selectedMonth$]).pipe(
      take(1),
      switchMap(([cp, selectedMonth]) => {
        if (!cp || !selectedMonth) {
          throw { error_code: ChargeRepayErrorCodes.GET_CSV_EXPORT_ERROR }
        }

        const monthStr = selectedMonth.month.toString().padStart(2, '0')
        const yearStr = selectedMonth.year.toString()

        return this.apiEmobility.getDriverCSV(cp.uuid, monthStr, yearStr)
      }),
      // Logic for successful data transformation
      map(({ csvData, filename }) => ({
        blob: new Blob([csvData], { type: 'text/csv' }),
        filename: filename,
      })),
      catchError((err: IChargeRepayAPIError) => {
        const error: IChargeRepayError = {
          severity: 'error',
          errorCode: err.error_code || ChargeRepayErrorCodes.GET_CSV_EXPORT_ERROR,
        }
        this.errorHandler.handleError(error)
        return throwError(() => error)
      })
    )
  }

  public getExportableDocuments$(): Observable<IExportData[]> {
    return new Observable<IExportData[]>((observer) => {
      this.apiEmobility.getExportDocuments().subscribe({
        next: (exDocs) => {
          observer.next(exDocs)
          observer.complete()
        },
        error: (err) => {
          const error: IChargeRepayError = {
            severity: 'error',
            errorCode: err.error_code,
          }
          this.errorHandler.handleError(error)

          observer.error(err)
          observer.complete()
        },
      })
    })
  }

  public isChargePointConnected() {
    return this.chargePointSubject$.pipe(
      take(1),
      filter((cP: IChargePoint) => !!cP),
      switchMap((chPnt: IChargePoint) => this.apiEmobility.getConnectionState(chPnt.endpoint_id))
    )
  }

  public getSpecificExport$(document: IExportData): Observable<IExportDocumentFile> {
    return new Observable<IExportDocumentFile>((observer) => {
      this.driver$.pipe(take(1)).subscribe({
        next: (driver) => {
          if (driver) {
            this.apiEmobility.getDriverExport(document.export_id, driver.user_id).subscribe({
              next: (file) => {
                observer.next(file)
                observer.complete()
              },
              error: () => {
                const error: IChargeRepayError = {
                  severity: 'error',
                  errorCode: PCIAMErrorCodes.GET_ORGANIZATION_SPECIFIC_EXPORT_ERROR,
                }
                this.errorHandler.handleError(error)

                observer.error(error)
                observer.complete()
              },
            })
          } else {
            const error: IChargeRepayError = {
              severity: 'error',
              errorCode: PCIAMErrorCodes.GET_ORGANIZATION_SPECIFIC_EXPORT_ERROR,
            }
            this.errorHandler.handleError(error)

            observer.error(error)
            observer.complete()
          }
        },
      })
    })
  }

  /*
   *   When the user want to see their uploaded contract,
   *   We fetch it here and open it in a new tab.
   */
  public getContractAndOpen(priceId: number) {
    this.chargePoint$
      .pipe(
        take(1),
        filter((cp): cp is IChargePoint => !!cp)
      )
      .subscribe({
        next: (cp) => {
          const chargePointUuid = cp.uuid
          this.apiEmobility.getChargePointEnergyContract(chargePointUuid, priceId).subscribe((blob) => {
            openImageInNewTab(blob)
          })
        },
      })
  }

  /*
   * Uploads the energy contract of the driver for currently active contracts
   * It will update the corresponding price information state
   */
  public uploadContract(base64String: string, price: string) {
    return this.chargePoint$.pipe(
      take(1),
      filter((cp): cp is IChargePoint => !!cp),
      switchMap((chargePoint) => this.uploadContractToApi(base64String, chargePoint, price)), // Switch map because of the async side effects
      switchMap((newPrice) => this.updateLatestPriceInformation(newPrice)),
      tap((updatedPriceInfo) => {
        if (!updatedPriceInfo) {
          const error: IChargeRepayError = {
            severity: 'error',
            errorCode: ChargeRepayErrorCodes.UPLOAD_ENERGY_CONTRACT_ERROR,
          }
          throwError(() => error)
        }
      })
    )
  }

  /*
   * Uploads the energy contract of the driver for currently active contracts
   * It will update the corresponding price information state
   */
  public uploadContractScheduled(base64String: string, price: string, timestamp: string) {
    return this.chargePoint$.pipe(
      take(1),
      filter((cp): cp is IChargePoint => !!cp),
      switchMap((chargePoint) => this.uploadContractToApi(base64String, chargePoint, price, timestamp)), // Switch map because of the async side effects
      switchMap((newPrice) => this.updateLatestUpcomingPriceInformation(newPrice)),
      tap((updatedPriceInfo) => {
        if (!updatedPriceInfo) {
          const error: IChargeRepayError = {
            severity: 'error',
            errorCode: ChargeRepayErrorCodes.UPLOAD_ENERGY_CONTRACT_ERROR,
          }
          throwError(() => error)
        }
      })
    )
  }

  private uploadContractToApi(base64String: string, chargePoint: IChargePoint, price: string, timestamp?: string): Observable<any> {
    const data = base64String.split(',')[1]
    const priceInCent = centToEuro(+price)

    if (timestamp) {
      return this.priceInformationSubject$.pipe(
        take(1),
        filter((priceInfo) => !!priceInfo),
        switchMap((priceInfo) => {
          if (priceInfo?.latest_upcoming_price.timestamp) {
            return this.apiEmobility.uploadEnergyContractScheduled(chargePoint.uuid, priceInfo.latest_upcoming_price.id, data, priceInCent, timestamp)
          } else {
            return this.apiEmobility.uploadEnergyContract(chargePoint.uuid, data, priceInCent, timestamp)
          }
        })
      )
    } else {
      return this.apiEmobility.uploadEnergyContract(chargePoint.uuid, data, priceInCent)
    }
  }

  // Updates the price information based on a new price
  private updateLatestPriceInformation(newPrice: IPrice | null) {
    return this.priceInformationSubject$.pipe(
      take(1),
      tap((pI) => {
        if (pI && newPrice) {
          const newPriceInformation = {
            ...pI!,
            latest_price: newPrice,
          }
          this.priceInformationSubject$.next(newPriceInformation)
        }
      })
    )
  }

  // Updates the upcoming price information based on a new price
  private updateLatestUpcomingPriceInformation(newPrice: IPrice | null) {
    return this.priceInformationSubject$.pipe(
      take(1),
      tap((pI) => {
        if (pI && newPrice) {
          const newPriceInformation = {
            ...pI!,
            latest_upcoming_price: newPrice,
          }
          this.priceInformationSubject$.next(newPriceInformation)
        }
      })
    )
  }

  public getChargePointConfig() {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP) => {
        if (!cP) {
          return of(null)
        }

        const uuid = cP.uuid

        return this.apiEmobility.getCurrentConfig(uuid).pipe(
          tap((config: IGatewayConfig) => {
            this.chargePointConfigSubject$.next(config)
          })
        )
      })
    )
  }

  public getChargePointDefaultConfig() {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP) => {
        if (!cP) {
          return of(null)
        }

        const uuid = cP.uuid

        return this.apiEmobility.getConfigDefault(uuid).pipe(
          tap((config: IGatewayConfig) => {
            this.chargePointDefaultConfigSubject$.next(config)
          })
        )
      })
    )
  }

  public setChargePointConfig(newConfig: IGatewayConfig) {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP) => {
        if (!cP) {
          return of(null)
        }

        const uuid = cP.uuid

        return this.apiEmobility.setConfig(uuid, newConfig).pipe(
          tap(() => {
            this.chargePointConfigSubject$.next(newConfig)
          })
        )
      })
    )
  }

  /**
   * -- on demand function, storing data locally after first call --
   * This gets the car of the current driver by token
   * There is a list coming back from system, but it is not planned to support more than one car per driver at the moment
   * Thus, only the first entry of the car is required
   */
  public createCarWithMileages$(): Observable<ICarWithMileages> {
    return this.apiEmobility.getCarsOfDriver().pipe(
      take(1),
      switchMap((cars: ICar[]) => {
        if (cars.length > 0) {
          const car = cars[0]
          return this.apiEmobility.getMileagesOfCar(car.car_id).pipe(
            map((mileages: IMileage[]) => ({
              car: car,
              mileages: mileages,
            }))
          )
        } else {
          return EMPTY
        }
      }),
      tap((carWithMileage: ICarWithMileages) => {
        this.carSubject$.next(carWithMileage)
      })
    )
  }

  /**
   * -- on demand function, storing data locally after first call --
   * This triggers the backend and adds a new mileage value to the current car
   * Once successful it will also add the mileage to the car subject (Optimistically)
   */
  public addMileage(km: number, dateString: string, newCar: boolean = false) {
    return this.carSubject$.pipe(
      take(1),
      filter((carWM) => !!carWM),
      switchMap((carWM: ICarWithMileages) =>
        this.apiEmobility.setMileage(carWM.car.car_id, km, dateString, newCar).pipe(
          tap((mileage: IMileage) => {
            carWM.mileages = [...carWM.mileages, mileage]
            this.carSubject$.next({ ...carWM })
          })
        )
      )
    )
  }

  /**
   * Initiates a charge for calibration law conformity charges
   */
  public initiateEichrechtCharge(active: boolean) {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP: IChargePoint) => {
        if (!cP) {
          return of(null)
        }

        return this.apiEmobility.putEichrechtCharge(cP.uuid, active).pipe(
          tap((eichrecht: { eichrecht: boolean }) => {
            cP.eichrecht_charging = eichrecht.eichrecht
            this.chargePointSubject$.next(cP) // Update the BehaviorSubject
          })
        )
      }),
      catchError((error: IChargeRepayAPIError) => {
        const eichrechtError: IChargeRepayError = {
          severity: 'error',
          errorCode: error.error_code,
        }
        return throwError(() => eichrechtError)
      })
    )
  }

  /**
   * Completes a charge for private or company related charges
   */
  public completeCharge(completedCharge: IChargeCompleted) {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP: IChargePoint) => {
        if (!cP) {
          return of(null)
        }

        return this.apiEmobility.completeCharge(completedCharge, cP.uuid).pipe(
          tap(() => {
            // Only change the value of the charge if the call was successful. We can't do it earlier.
            completedCharge.charge.car_type = completedCharge.chargeType
          })
        )
      }),
      catchError((error: IChargeRepayAPIError) => {
        const completionError: IChargeRepayError = {
          severity: 'error',
          errorCode: error.error_code,
        }
        return throwError(() => completionError)
      })
    )
  }

  public updateCharges() {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP: IChargePoint) => {
        if (!cP) {
          return of(null)
        }

        return this.apiEmobility.getCharges(cP.uuid).pipe(
          filter((charges: ICharge[]) => !!charges),
          tap((charges: ICharge[]) => {
            const sortedCharges = charges.sort((a, b) => {
              return new Date(b.transaction_start).getTime() - new Date(a.transaction_start).getTime()
            })

            this.chargesSubject$.next(sortedCharges)
          })
        )
      }),
      catchError((error: IChargeRepayAPIError) => {
        const err: IChargeRepayError = {
          severity: 'error',
          errorCode: error.error_code,
        }

        return throwError(() => err)
      })
    )
  }

  /**
   * Returns the config code, which will only happening if there is a charge point.
   * If there is no charge point an onboarding needs to be done and returning null here is correct
   * TODO: make sure that there is an order completed
   */
  public createConfigCode$() {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP) => {
        if (!cP) {
          return of(null)
        }

        return this.apiEmobility.getConfigCode().pipe(
          tap((cCode: IConfigCode) => {
            if (cCode) {
              this.configurationCodeSubject$.next(cCode)
            }
          })
        )
      })
    )
  }

  public addRFIDTag(rfidTag: string) {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cP) => {
        if (!cP) {
          return of(null)
        }

        // Don`t update optimistically here since the rfidTag is more than a simple string
        // Get the list of tags instead from the backend.
        return this.apiEmobility.setRFIDTag(cP.uuid, rfidTag).pipe(
          tap(() => {
            this.createRFIDTags$().pipe(take(1)).subscribe()
          })
        )
      })
    )
  }

  public setBackendProviderRFIDTag(rfidTag: string) {
    return this.chargePoint$.pipe(
      take(1),
      switchMap((cp: IChargePoint) => {
        if (!cp) {
          throw new Error('No charge point available')
        }
        return this.apiEmobility.setBackendProviderRFIDTag(rfidTag, cp.uuid)
      }),
      tap(() => {
        // we optimistically update the charge point here and access the property directly which is ok since we checked it
        const cp = this.chargePointSubject$.getValue()
        if (cp) {
          this.chargePointSubject$.next({ ...cp, provider_id_tag: rfidTag })
        }
      })
    )
  }

  public setCompanyCarOnly(chargePointUuid: string, companyCarOnly: boolean) {
    return this.apiEmobility.setCompanyCarOnly(chargePointUuid, companyCarOnly).pipe(
      switchMap(() => this.apiEmobility.getChargePoints()),
      tap((chargePoints: IChargePoint[]) => {
        // reloading the charge point after 2 seconds is essential because of async backend processes.
        if (chargePoints && chargePoints.length > 0) {
          setTimeout(() => {
            this.chargePointSubject$.next(chargePoints[0])
          }, 2000)
        }
      }),
      catchError(() => {
        const err: IChargeRepayError = {
          severity: 'error',
          errorCode: ChargeRepayErrorCodes.SET_COMPANY_CAR_ONLY_ERROR,
        }

        return throwError(() => err)
      })
    )
  }
}
